import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import { motion } from 'framer-motion';
import { Calendar, Loader2, Clock } from 'lucide-react';
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns';
import { nl, enUS } from 'date-fns/locale';
import { useInvoiceStore } from '../../stores/invoiceStore';
import { DateRangePicker, DateRange } from './DateRangePicker';

export type PeriodType = 'month' | 'custom' | 'planned';

export interface Period {
  type: PeriodType;
  label: string;
  value: string;
}

interface PeriodSelectorFilterProps {
  currentPeriod: string;
  onPeriodChange: (period: string) => void;
  isLoading?: boolean;
}

export const PeriodSelectorFilter: React.FC<PeriodSelectorFilterProps> = ({
  currentPeriod,
  onPeriodChange,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const language = useInvoiceStore((state) => state.settings.language);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [customDateRange, setCustomDateRange] = useState<DateRange | null>(null);
  const [showLoading, setShowLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Delay showing the loading indicator
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    
    if (isLoading) {
      timer = setTimeout(() => {
        setShowLoading(true);
      }, 200);
    } else {
      setShowLoading(false);
    }
    
    return () => {
      clearTimeout(timer);
    };
  }, [isLoading]);

  // Generate periods: Current Month, Previous Month, Custom, Planned
  const periods = React.useMemo(() => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    
    // Previous month date
    const prevMonth = subMonths(now, 1);
    const prevMonthYear = prevMonth.getFullYear();
    const prevMonthNum = prevMonth.getMonth();
    
    // Format month names based on locale
    const monthFormat = 'MMMM';
    const locale = language === 'nl' ? nl : enUS;
    let currentMonthName = format(now, monthFormat, { locale });
    let prevMonthName = format(prevMonth, monthFormat, { locale });
    
    // Ensure first letter is capitalized
    currentMonthName = currentMonthName.charAt(0).toUpperCase() + currentMonthName.slice(1);
    prevMonthName = prevMonthName.charAt(0).toUpperCase() + prevMonthName.slice(1);
    
    return [
      // Planned option (moved to leftmost position)
      {
        type: 'planned' as const,
        label: t('payment.filter.planned'),
        value: 'planned',
      },
      // Current month
      {
        type: 'month' as const,
        label: currentMonthName,
        value: `month-${currentYear}-${String(currentMonth + 1).padStart(2, '0')}`,
      },
      // Previous month
      {
        type: 'month' as const,
        label: prevMonthName,
        value: `month-${prevMonthYear}-${String(prevMonthNum + 1).padStart(2, '0')}`,
      },
      // Custom option
      {
        type: 'custom' as const,
        label: t('period.custom'),
        value: 'custom',
      }
    ];
  }, [t, language]);

  const handlePeriodChange = (periodValue: string) => {
    if (periodValue === 'custom') {
      setIsDatePickerOpen(true);
    } else {
      onPeriodChange(periodValue);
    }
  };

  const handleDateRangeApply = (range: DateRange) => {
    setCustomDateRange(range);
    setIsDatePickerOpen(false);
    
    // Format the custom range as a string: custom-YYYY-MM-DD-YYYY-MM-DD
    const formattedRange = `custom-${format(range.startDate, 'yyyy-MM-dd')}-${format(range.endDate, 'yyyy-MM-dd')}`;
    onPeriodChange(formattedRange);
  };

  const getCustomRangeLabel = () => {
    if (!customDateRange) return t('period.custom');
    
    const { startDate, endDate } = customDateRange;
    const dateFormat = language === 'nl' ? 'dd MMM' : 'MMM dd';
    const locale = language === 'nl' ? nl : enUS;
    
    return `${format(startDate, dateFormat, { locale })} - ${format(endDate, dateFormat, { locale })}`;
  };

  return (
    <div className="mb-4">
      <div className="relative">
        <div className="flex items-center gap-1 sm:gap-2 p-1 bg-gray-100/50 backdrop-blur-xl rounded-xl whitespace-nowrap min-w-full xs:min-w-0 sm:min-w-0 overflow-x-auto hide-scrollbar">
          {periods.map((period) => {
            const isActive = currentPeriod === period.value || 
                            (period.type === 'custom' && currentPeriod.startsWith('custom-'));
            
            return (
              <button
                key={period.value}
                onClick={() => handlePeriodChange(period.value)}
                disabled={isLoading}
                className={`relative flex-shrink-0 xs:flex-1 px-1.5 xs:px-2 sm:px-3 py-1.5 text-xs sm:text-sm font-medium rounded-lg transition-all duration-200 ${
                  isActive
                    ? 'text-primary-700 bg-white shadow-sm z-10'
                    : 'text-gray-600 hover:text-primary-600 hover:bg-white/50'
                } ${isLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
              >
                {isActive && (
                  <motion.div
                    layoutId="activePeriod"
                    className="absolute inset-0 bg-white rounded-lg shadow-sm"
                    transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                  />
                )}
                <div className="relative flex items-center justify-center">
                  {period.type === 'custom' ? (
                    <div className="flex items-center justify-center">
                      <Calendar className="w-3 h-3 mr-1" />
                      <span>
                        {currentPeriod.startsWith('custom-') && customDateRange 
                          ? getCustomRangeLabel() 
                          : period.label}
                      </span>
                    </div>
                  ) : period.type === 'planned' ? (
                    <div className="flex items-center justify-center">
                      <Clock className="w-3 h-3 mr-1" />
                      <span>{period.label}</span>
                    </div>
                  ) : (
                    <span className="font-medium">{period.label}</span>
                  )}
                </div>
                
                {/* Loading indicator in fixed position */}
                {showLoading && isActive && (
                  <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                    <Loader2 className="w-3 h-3 animate-spin" />
                  </div>
                )}
              </button>
            );
          })}
        </div>
        
        {/* Date Range Picker */}
        <div ref={containerRef} className="relative">
          <DateRangePicker
            isOpen={isDatePickerOpen}
            onClose={() => setIsDatePickerOpen(false)}
            onApply={handleDateRangeApply}
            initialRange={customDateRange || {
              startDate: startOfMonth(new Date()),
              endDate: endOfMonth(new Date())
            }}
          />
        </div>
      </div>
    </div>
  );
}; 